import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import api from '../lib/api';
import { Breadcrumb } from '../components/Breadcrumb';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import {
  useCreateWorkGroup,
  useCreateWorkGroupFunction,
  useDeleteWorkGroup,
  useDeleteWorkGroupFunction,
  useReassignOpenTasksForSlot,
  useUpdateWorkGroup,
  useUpdateWorkGroupFunction,
  useUpsertWorkGroupSlot,
  useWorkGroup,
  useWorkGroupFunctions,
  useWorkGroups,
} from '../hooks/useWorkGroups';
import {
  activeSlotMembers,
  catalogSlots,
  getWorkGroupErrorMessage,
  shouldOfferSlotReassignment,
  shouldDenyWorkGroupsAccess,
} from './workGroupSettings';

export const Route = createFileRoute('/w/$workspaceId/settings/work-groups')({
  component: WorkGroupsPage,
});

interface MemberOption {
  id: string;
  status?: string;
  user?: { name?: string | null; email?: string | null };
}

const buttonClass =
  'px-4 py-2 rounded-[10px] text-xs font-semibold transition-opacity hover:opacity-80 disabled:opacity-50';
const inputClass =
  'w-full min-h-10 px-3 py-2 rounded-[10px] border border-gray-200 bg-white text-xs text-gray-700 outline-none focus:border-gray-700';

function WorkGroupsPage() {
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);
  const canManage = hasPermission('work_groups.manage');
  const [selectedGroupId, setSelectedGroupId] = useState<string>();
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showCreateFunction, setShowCreateFunction] = useState(false);
  const [renameFunction, setRenameFunction] = useState<{ id: string; name: string }>();
  const [error, setError] = useState<string>();
  const [slotReassignment, setSlotReassignment] = useState<{
    count: number;
    groupId: string;
    functionId: string;
    newMembershipId: string;
    previousMembershipId: string;
  }>();

  useEffect(() => {
    if (!shouldDenyWorkGroupsAccess(isLoadingPermissions, canManage, location.pathname)) return;
    showPermissionDenied('Gerenciar grupos de trabalho');
    navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
  }, [
    canManage,
    isLoadingPermissions,
    location.pathname,
    navigate,
    showPermissionDenied,
    workspaceId,
  ]);

  const { data: workspace } = useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}`)).data.data,
    enabled: canManage,
  });
  const { data: members = [] } = useQuery<MemberOption[]>({
    queryKey: ['members', workspaceId],
    queryFn: async () => (await api.get('/members')).data.data,
    enabled: canManage,
  });
  const groupsQuery = useWorkGroups({ enabled: canManage });
  const functionsQuery = useWorkGroupFunctions({ enabled: canManage });
  const groupQuery = useWorkGroup(selectedGroupId);
  const createGroup = useCreateWorkGroup();
  const updateGroup = useUpdateWorkGroup();
  const deleteGroup = useDeleteWorkGroup();
  const createFunction = useCreateWorkGroupFunction();
  const updateFunction = useUpdateWorkGroupFunction();
  const deleteFunction = useDeleteWorkGroupFunction();
  const upsertSlot = useUpsertWorkGroupSlot();
  const reassignOpenTasks = useReassignOpenTasksForSlot();

  useEffect(() => {
    if (!selectedGroupId && groupsQuery.data?.length) {
      setSelectedGroupId(groupsQuery.data[0].id);
    }
    if (
      selectedGroupId &&
      groupsQuery.data &&
      !groupsQuery.data.some((group) => group.id === selectedGroupId)
    ) {
      setSelectedGroupId(groupsQuery.data[0]?.id);
    }
  }, [groupsQuery.data, selectedGroupId]);

  const slotRows = useMemo(
    () => catalogSlots(functionsQuery.data ?? [], groupQuery.data?.slots ?? []),
    [functionsQuery.data, groupQuery.data?.slots]
  );
  const activeMembers = useMemo(
    () => activeSlotMembers(members),
    [members]
  );

  const guard = () => requirePermission('work_groups.manage', 'Gerenciar grupos de trabalho');
  const reportError = (caught: unknown, fallback: string) =>
    setError(getWorkGroupErrorMessage(caught, fallback));

  if (isLoadingPermissions || !canManage) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-8 py-6">
        <Breadcrumb
          items={[
            {
              label: workspace?.name || 'Workspace',
              to: '/w/$workspaceId/clients',
              params: { workspaceId },
            },
            {
              label: 'Configurações',
              to: '/w/$workspaceId/settings/members',
              params: { workspaceId },
            },
            { label: 'Grupos de Trabalho' },
          ]}
        />

        {error && (
          <div
            role="alert"
            className="mt-6 flex items-center justify-between rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700"
          >
            <span>{error}</span>
            <button onClick={() => setError(undefined)} aria-label="Fechar erro">
              ×
            </button>
          </div>
        )}

        <section className="bg-white p-6 mt-6 rounded-[15px] shadow-sm">
          <PageHeading
            title="Grupos de Trabalho"
            description="Organize responsáveis por função em cada equipe"
            action={
              <button
                className={`${buttonClass} bg-[#06141B] text-white`}
                onClick={() => guard() && setShowCreateGroup(true)}
              >
                Novo grupo
              </button>
            }
          />

          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
            <aside className="space-y-2">
              {groupsQuery.isLoading ? (
                <EmptyText>Carregando grupos...</EmptyText>
              ) : groupsQuery.data?.length ? (
                groupsQuery.data.map((group) => (
                  <button
                    key={group.id}
                    onClick={() => setSelectedGroupId(group.id)}
                    className="w-full text-left p-4 rounded-[10px] transition-colors"
                    style={{
                      backgroundColor: selectedGroupId === group.id ? '#06141B' : '#F9FAFB',
                      color: selectedGroupId === group.id ? '#FFFFFF' : '#2D3748',
                    }}
                  >
                    <strong className="block text-sm">{group.name}</strong>
                    <span className="block mt-1 text-[11px] opacity-70">
                      {group.description || 'Sem descrição'}
                    </span>
                  </button>
                ))
              ) : (
                <EmptyText>Nenhum grupo criado.</EmptyText>
              )}
            </aside>

            <div className="min-w-0">
              {groupQuery.isLoading ? (
                <EmptyText>Carregando grupo...</EmptyText>
              ) : groupQuery.data ? (
                <GroupEditor
                  group={groupQuery.data}
                  rows={slotRows}
                  members={activeMembers}
                  saving={updateGroup.isPending || upsertSlot.isPending}
                  onSave={async (name, description) => {
                    if (!guard()) return;
                    setError(undefined);
                    try {
                      await updateGroup.mutateAsync({
                        id: groupQuery.data.id,
                        payload: { name, description: description || null },
                      });
                    } catch (caught) {
                      reportError(caught, 'Erro ao atualizar grupo');
                    }
                  }}
                  onDelete={async () => {
                    if (!guard() || !window.confirm(`Excluir o grupo "${groupQuery.data.name}"?`))
                      return;
                    setError(undefined);
                    try {
                      await deleteGroup.mutateAsync(groupQuery.data.id);
                    } catch (caught) {
                      reportError(caught, 'Erro ao excluir grupo');
                    }
                  }}
                  onSlotChange={async (functionId, membershipId) => {
                    if (!guard()) return;
                    setError(undefined);
                    try {
                      const previousMembershipId =
                        groupQuery.data.slots.find((slot) => slot.functionId === functionId)
                          ?.membershipId ?? null;
                      const result = await upsertSlot.mutateAsync({
                        groupId: groupQuery.data.id,
                        functionId,
                        payload: { membershipId: membershipId || null },
                      });
                      if (
                        shouldOfferSlotReassignment(
                          result.openTasksCount,
                          previousMembershipId,
                          membershipId,
                        ) &&
                        previousMembershipId
                      ) {
                        setSlotReassignment({
                          count: result.openTasksCount,
                          groupId: groupQuery.data.id,
                          functionId,
                          newMembershipId: membershipId,
                          previousMembershipId,
                        });
                      }
                    } catch (caught) {
                      reportError(caught, 'Erro ao atualizar responsável');
                    }
                  }}
                />
              ) : (
                <EmptyText>Selecione ou crie um grupo para editar.</EmptyText>
              )}
            </div>
          </div>
        </section>

        <section className="bg-white p-6 mt-6 rounded-[15px] shadow-sm">
          <PageHeading
            title="Catálogo de Funções"
            description="Estas funções aparecem como slots em todos os grupos"
            action={
              <button
                className={`${buttonClass} bg-[#06141B] text-white`}
                onClick={() => guard() && setShowCreateFunction(true)}
              >
                Nova função
              </button>
            }
          />
          <div className="divide-y divide-gray-100">
            {functionsQuery.data?.map((workFunction) => (
              <div key={workFunction.id} className="flex items-center justify-between py-3">
                <span className="text-sm font-medium text-gray-700">{workFunction.name}</span>
                <div className="flex gap-2">
                  <button
                    className={`${buttonClass} bg-gray-50 text-gray-700`}
                    onClick={() =>
                      guard() &&
                      setRenameFunction({
                        id: workFunction.id,
                        name: workFunction.name,
                      })
                    }
                  >
                    Renomear
                  </button>
                  <button
                    className={`${buttonClass} bg-red-50 text-red-600`}
                    onClick={async () => {
                      if (!guard() || !window.confirm(`Excluir a função "${workFunction.name}"?`))
                        return;
                      setError(undefined);
                      try {
                        await deleteFunction.mutateAsync(workFunction.id);
                      } catch (caught) {
                        reportError(caught, 'Erro ao excluir função');
                      }
                    }}
                  >
                    Excluir
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {showCreateGroup && (
        <NameDescriptionModal
          title="Novo Grupo de Trabalho"
          saving={createGroup.isPending}
          onClose={() => setShowCreateGroup(false)}
          onSave={async (name, description) => {
            if (!guard()) return;
            try {
              const created = await createGroup.mutateAsync({
                name,
                description: description || null,
              });
              setSelectedGroupId(created.id);
              setShowCreateGroup(false);
            } catch (caught) {
              reportError(caught, 'Erro ao criar grupo');
            }
          }}
        />
      )}
      {showCreateFunction && (
        <NameModal
          title="Nova Função"
          saving={createFunction.isPending}
          onClose={() => setShowCreateFunction(false)}
          onSave={async (name) => {
            if (!guard()) return;
            const count = groupsQuery.data?.length ?? 0;
            const propagateToExistingGroups =
              count > 0 && window.confirm(`Adicionar aos ${count} grupos existentes?`);
            try {
              await createFunction.mutateAsync({
                name,
                propagateToExistingGroups,
              });
              setShowCreateFunction(false);
            } catch (caught) {
              reportError(caught, 'Erro ao criar função');
            }
          }}
        />
      )}
      {renameFunction && (
        <NameModal
          title="Renomear Função"
          initialName={renameFunction.name}
          saving={updateFunction.isPending}
          onClose={() => setRenameFunction(undefined)}
          onSave={async (name) => {
            if (!guard()) return;
            try {
              await updateFunction.mutateAsync({
                id: renameFunction.id,
                payload: { name },
              });
              setRenameFunction(undefined);
            } catch (caught) {
              reportError(caught, 'Erro ao renomear função');
            }
          }}
        />
      )}
      {slotReassignment && (
        <Modal title="Responsável atualizado" onClose={() => setSlotReassignment(undefined)}>
          <p className="text-xs leading-5 text-gray-700">
            O slot foi atualizado. Existem {slotReassignment.count} tarefas abertas atribuídas ao
            responsável anterior. Deseja reatribuí-las ao novo responsável?
          </p>
          <div className="flex justify-end gap-3 pt-5">
            <button
              className={`${buttonClass} border border-gray-200 text-gray-700`}
              onClick={() => setSlotReassignment(undefined)}
            >
              Não
            </button>
            <button
              className={`${buttonClass} bg-[#06141B] text-white`}
              disabled={!slotReassignment.newMembershipId || reassignOpenTasks.isPending}
              onClick={async () => {
                if (!guard() || !slotReassignment.newMembershipId) return;
                try {
                  await reassignOpenTasks.mutateAsync({
                    groupId: slotReassignment.groupId,
                    functionId: slotReassignment.functionId,
                    payload: {
                      newMembershipId: slotReassignment.newMembershipId,
                      previousMembershipId: slotReassignment.previousMembershipId,
                    },
                  });
                  setSlotReassignment(undefined);
                } catch (caught) {
                  reportError(caught, 'Erro ao reatribuir tarefas');
                }
              }}
            >
              {reassignOpenTasks.isPending ? 'Reatribuindo...' : 'Sim, reatribuir'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function GroupEditor({
  group,
  rows,
  members,
  saving,
  onSave,
  onDelete,
  onSlotChange,
}: {
  group: { id: string; name: string; description: string | null };
  rows: ReturnType<typeof catalogSlots>;
  members: MemberOption[];
  saving: boolean;
  onSave: (name: string, description: string) => void;
  onDelete: () => void;
  onSlotChange: (functionId: string, membershipId: string) => void;
}) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? '');

  useEffect(() => {
    setName(group.name);
    setDescription(group.description ?? '');
  }, [group.id, group.name, group.description]);

  return (
    <div>
      <form
        className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-6 border-b border-gray-100"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) onSave(name.trim(), description.trim());
        }}
      >
        <label className="text-xs font-medium text-gray-700">
          Nome
          <input
            className={`${inputClass} mt-1`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </label>
        <label className="text-xs font-medium text-gray-700">
          Descrição
          <input
            className={`${inputClass} mt-1`}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        <div className="md:col-span-2 flex justify-between">
          <button
            type="button"
            className={`${buttonClass} bg-red-50 text-red-600`}
            onClick={onDelete}
          >
            Excluir grupo
          </button>
          <button
            type="submit"
            disabled={saving}
            className={`${buttonClass} bg-[#06141B] text-white`}
          >
            Salvar grupo
          </button>
        </div>
      </form>

      <h3 className="mt-6 mb-3 text-sm font-semibold text-gray-800">Responsáveis por função</h3>
      <div className="overflow-x-auto rounded-[10px] border border-gray-100">
        <table className="w-full text-left">
          <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3 font-semibold">Função</th>
              <th className="px-4 py-3 font-semibold">Responsável</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={row.functionId}>
                <td className="px-4 py-3">
                  <span className="text-xs font-medium text-gray-700">{row.functionName}</span>
                  {!row.slot && <span className="ml-2 text-[10px] text-amber-600">sem slot</span>}
                </td>
                <td className="px-4 py-3">
                  <select
                    aria-label={`Responsável por ${row.functionName}`}
                    className={inputClass}
                    value={row.membershipId ?? ''}
                    disabled={saving}
                    onChange={(event) => onSlotChange(row.functionId, event.target.value)}
                  >
                    <option value="">Sem responsável</option>
                    {members.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.user?.name || member.user?.email || 'Membro sem nome'}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PageHeading({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 mb-6">
      <div>
        <h1 className="text-lg font-semibold text-[#2D3748]">{title}</h1>
        <p className="mt-1 text-xs text-[#A0AEC0]">{description}</p>
      </div>
      {action}
    </div>
  );
}

function EmptyText({ children }: { children: ReactNode }) {
  return <p className="p-6 text-center text-xs text-gray-500">{children}</p>;
}

function NameDescriptionModal({
  title,
  saving,
  onClose,
  onSave,
}: {
  title: string;
  saving: boolean;
  onClose: () => void;
  onSave: (name: string, description: string) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  return (
    <Modal title={title} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) onSave(name.trim(), description.trim());
        }}
      >
        <label className="block text-xs font-medium text-gray-700">
          Nome
          <input
            autoFocus
            className={`${inputClass} mt-1`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </label>
        <label className="block text-xs font-medium text-gray-700">
          Descrição
          <textarea
            className={`${inputClass} mt-1 min-h-20`}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        <ModalActions saving={saving} onClose={onClose} />
      </form>
    </Modal>
  );
}

function NameModal({
  title,
  initialName = '',
  saving,
  onClose,
  onSave,
}: {
  title: string;
  initialName?: string;
  saving: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState(initialName);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim()) onSave(name.trim());
  };
  return (
    <Modal title={title} onClose={onClose}>
      <form className="space-y-4" onSubmit={submit}>
        <label className="block text-xs font-medium text-gray-700">
          Nome
          <input
            autoFocus
            className={`${inputClass} mt-1`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </label>
        <ModalActions saving={saving} onClose={onClose} />
      </form>
    </Modal>
  );
}

function ModalActions({ saving, onClose }: { saving: boolean; onClose: () => void }) {
  return (
    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
      <button
        type="button"
        className={`${buttonClass} border border-gray-200 text-gray-700`}
        onClick={onClose}
        disabled={saving}
      >
        Cancelar
      </button>
      <button type="submit" className={`${buttonClass} bg-[#06141B] text-white`} disabled={saving}>
        {saving ? 'Salvando...' : 'Salvar'}
      </button>
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-[15px] bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 p-5">
          <h2 className="text-lg font-semibold text-[#06141B]">{title}</h2>
          <button
            className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-[#06141B] text-white"
            onClick={onClose}
            aria-label="Fechar"
          >
            ×
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}
